// import axios from 'axios';
import PersonalDashboardPopup from '@/components/PersonalDashboardPopup.vue';
import {bookmarkList} from '@/library/bookmark';
import LocalStorage from '@/library/localStorage';
import {isVueNavigationFailure, setRouter} from '@/library/utils';
import axios from 'axios';
import {type Component as VueComponent, markRaw} from 'vue';
import {createRouter, createWebHashHistory, createWebHistory, type NavigationGuardNext, type RouteLocation, type RouteRecordRaw} from 'vue-router';
import {exportEnabled} from '../library/export';
import FlashMessenger from '../library/flashMessenger';
import Loader from '../library/intranet/loader';
import {HasNotifications} from '../library/notification';
import {closeAllPopUps, Popup} from '../library/popup';
import TopMessageHandler from '../library/topMessageHandler';
import {isAllowedModule} from '../library/user';
import XHRManager from '../library/xhrManager';
import store from '../store';
import {deepCopyObject, getLoginReferrer, XHR_STATUS_DEFAULT} from '../utils/portuna';
import absences from './absences';
import appointment from './appointment';
import chat from './chat';
import codeReview from './code-review';
import contact from './contact';
import customer from './customer';
import dashboard from './dashboard';
import defaultRoutes from './default';
import education from './education';
import employee from './employee';
import enrolment from './enrolment';
import event from './event';
import faq from './faq';
import finance from './finance';
import gallery from './gallery';
import globalNews from './global-news';
import installer from './installer';
import issueTracker from './issue-tracker';
import log from './log';
import member from './member';
import navigation from './navigation';
import news from './news';
import notification from './notification';
import page from './page';
import pms from './pms';
import poll from './poll';
import procurement from './procurement';
import equipment from './reservation/equipment';
import roommanagement from './reservation/roommanagement';
import sportsarea from './reservation/sportsarea';
import vehicle from './reservation/vehicle';
import school from './school';
import services from './services';
import sessionservice from './sessionservice';
import settings from './settings';
import shiftSchedule from './shift-schedule';
import sickNote from './sick-note';
import task from './task';
import timeTracker from './time-tracker';
import training from './training';
import useradministration from './useradministration';
import vacation from './vacation';
import whistleblower from './whistleblower';
import testing from './testing';

const hash = window.location.hash;

const routes: RouteRecordRaw[] = [];

// module routes
routes.push(...news);
routes.push(...enrolment);
routes.push(...notification);
routes.push(...settings);
routes.push(...gallery);
routes.push(...roommanagement);
routes.push(...vehicle);
routes.push(...sportsarea);
routes.push(...equipment);
routes.push(...member);
routes.push(...page);
routes.push(...task);
routes.push(...finance);
routes.push(...navigation);
routes.push(...employee);
routes.push(...appointment);
routes.push(...timeTracker);
routes.push(...school);
routes.push(...vacation);
routes.push(...sickNote);
routes.push(...absences);
routes.push(...education);
routes.push(...event);
routes.push(...poll);
routes.push(...useradministration);
routes.push(...chat);
routes.push(...shiftSchedule);
routes.push(...services);
routes.push(...customer);
routes.push(...dashboard);
routes.push(...faq);
routes.push(...globalNews);
routes.push(...training);
routes.push(...sessionservice);
routes.push(...contact);
routes.push(...pms);
routes.push(...installer);
routes.push(...issueTracker);
routes.push(...procurement);
routes.push(...codeReview);
routes.push(...whistleblower);
routes.push(...log);
routes.push(...testing);

// default routes
routes.push(...defaultRoutes);

let router = createRouter({
    history: !exportEnabled() ? createWebHistory() : createWebHashHistory(),
    routes,
});

let redirectNextRoute    = false;
let nextRoute: any       = null;
let employeePromptOpened = false;

function redirectToRoute()
{
    if (nextRoute && typeof nextRoute === 'object')
    {
        window.location.pathname = nextRoute.fullPath || nextRoute.path;
    }
    else if (typeof nextRoute === 'string')
    {
        window.location.pathname = nextRoute;
    }
    else
    {
        window.location.reload();
    }
}

// do something on vue-router error
router.onError((err: Error) =>
{
    // redirect/reload page if chunk cant be loaded
    if (
        err.name === 'ChunkLoadError'
        || err.message.includes('Failed to fetch dynamically imported module')
        || err.message.includes('error loading dynamically imported module')
    )
    {
        redirectToRoute();
    }
    else
    {
        console.error('another route error', err);
    }
});

router.beforeEach(async function(to: RouteLocation, from: RouteLocation, vueNext: NavigationGuardNext)
{
    if (to.name === 'logout')
    {
        return store.dispatch('logout', router).finally(() => vueNext());
    }

    const toMeta = to.meta as MetaData;

    if (!exportEnabled() && HasNotifications && Notification.permission === 'default' && !toMeta?.disableNotifications)
    {
        // Notification.requestPermission() returns sometime undefined [Safari] - maybe disabled notification setting?
        // so don't use .catch/.then
        try
        {
            await Notification.requestPermission();
        }
        catch (e)
        {
            console.error(e);
        }
    }

    // prevent php redirect - mark as read - and redirect to target url
    if (to.name === 'notificationConfirmRead')
    {
        Loader.show();

        axios.post(to.path).then(({data: {data}}) =>
        {
            store.dispatch('notification/markAsRead', parseInt(to.params.id as string));

            if (router.currentRoute.value.fullPath !== data.entryLink)
            {
                return router.push(data.entryLink);
            }

            return;
        })
        .catch(error =>
        {
            if (!isVueNavigationFailure(error))
            {
                TopMessageHandler.error('Die Benachrichtigung konnte nicht gefunden werden.');
                console.error(error);
            }
        })
        .finally(Loader.hide);

        return vueNext(false);
    }

    // download-context -> keep active application
    else if (to.path && to.path.indexOf('/data/') === 0)
    {
        window.location.assign(to.path);
        return vueNext(false);
    }

    const next = (redirect?: any) =>
    {
        nextRoute = redirect || to;
        if (redirectNextRoute)
        {
            redirectToRoute();
        }
        else
        {
            vueNext(redirect);
        }
    };

    // load layout data if not loaded
    if (!store.state.layoutDataLoaded)
    {
        try
        {
            await store.dispatch('hideApp', true);
            Loader.show(null);

            let data = await store.dispatch('getLayoutData');
            await store.dispatch('hideApp', false);

            if (data.user)
            {
                store.commit('login');
            }
        }
        catch (e)
        {
            console.log(e); // TODO: Remove
            // handle error
        }
        Loader.hide();
    }

    // resets on route change
    if (from.name !== to.name)
    {
        if (!from.meta.noResetIfSame || !toMeta.noResetIfSame || from.meta.noResetIfSame !== toMeta.noResetIfSame)
        {
            // reset form errors
            store.commit('form/reset');

            // reset flash messages on leave route
            FlashMessenger.resetMessages();
        }

        closeAllPopUps();
    }

    if (!exportEnabled() && toMeta.module && 'employee' === toMeta.module)
    {
        const isAllowedEmployee = isAllowedModule('employee');

        if (!toMeta.onlyEmployeeId && !isAllowedEmployee)
        {
            return next({name: store.state.layoutData.user.employeeId ? 'employee-home' : 'home'});
        }
        else if (toMeta.onlyEmployeeId && !store.state.layoutData.user.employeeId)
        {
            return next({name: isAllowedEmployee ? 'employee-dashboard' : 'home'});
        }
    }

    // assistant
    if (!exportEnabled() && toMeta?.assistant?.key && store.state.layoutData.assistant[toMeta.assistant?.key])
    {
        if (toMeta.assistant?.key === 'time_tracker' && !store.state.layoutData.employee_configuration!.sub_module.includes('absences'))
        {
            return next();
        }

        const assistantStartRedirect = toMeta.assistant.redirect;

        if (assistantStartRedirect && to.name !== assistantStartRedirect.name!)
        {
            return next(assistantStartRedirect);
        }

        // only admins are allowed to use the assistant
        if (!store.state.layoutData.user.admin)
        {
            TopMessageHandler.info('Die Ersteinrichtung des Moduls durch einen Administrator wurde noch nicht vorgenommen.');
            return next({name: 'home'});
        }

        const assistantSteps: Partial<PopupOptions>[] = toMeta.assistant.steps;

        let proceedAssistant = async (step = 0) =>
        {
            if (step === assistantSteps.length)
            {
                if (!toMeta.assistant)
                {
                    return;
                }

                const onComplete = toMeta.assistant.onComplete;

                if (onComplete)
                {
                    window.location.replace(router.resolve(onComplete).href);
                }

                return;
            }

            const popUpOptions = assistantSteps[step];

            const popup = await store.dispatch('createPopup', Object.assign({}, popUpOptions, {
                closeable: false,
                buttons: [],
                size: 'medium',
                component: markRaw((await popUpOptions.component()).default),
                componentProps: Object.assign({
                    events: {
                        proceed: () =>
                        {
                            proceedAssistant(step + 1);
                            popup.close();
                        },
                    }
                }, popUpOptions.componentProps || {})
            }));

            popup.open().then();
        };

        proceedAssistant().then();
    }
    // redirect user to home if user is logged in and want access to a page where must be logged out
    if (toMeta.onlyLoggedOut && store.state.loggedIn)
    {
        if (to.params.referrer)
        {
            return next(atob(to.params.referrer as string));
        }
        else
        {
            return next({name: 'home'});
        }
    }
    else if (!toMeta.allowLoggedOut && !store.state.loggedIn)
    {
        return next(getLoginReferrer(to));
    }

    if (store.state.loggedIn && !exportEnabled() && (to.meta?.layout ?? '') !== 'blank')
    {
        axios.post('/bookmark/bookmark/bookmark-is', {url: to.path}).then((response) =>
        {
            if (response.data.status === XHR_STATUS_DEFAULT)
            {
                store.commit('bookmarkSetId', response.data.data.entry.id);
                store.commit('personalDashboard/setNeeds', Boolean(response.data.data.needsPersonalDashboardPopup));
            }
        }).catch((error) =>
        {
            store.commit('bookmarkSetId', 0);
            console.error('[Bookmark response]', error.response);
        });

        if (LocalStorage.get('bookmarkForceOpen', false))
        {
            LocalStorage.set('bookmarkForceOpen', false);
            bookmarkList();
        }

        let employeePrompt: IEmployeePrompt                    = store.state.layoutData.employee_up_to_date;
        const employeePromptStorage: Nullable<IEmployeePrompt> = LocalStorage.get('employeeUpdateData');

        if (
            !employeePromptOpened && typeof employeePrompt !== 'undefined' && employeePrompt.prompt
            // if closeable and closed before -> don't open
            && (!employeePrompt.closeable || !employeePromptStorage || employeePromptStorage.date !== employeePrompt.date || employeePromptStorage.prompt)
        )
        {
            employeePromptOpened = true;

            const EmployeeUpdateData = () => import('../views/employee/EmployeeUpdateData.vue');

            EmployeeUpdateData().then((Instance: { default: VueComponent }) =>
            {
                const closeable = employeePrompt.closeable;

                // sync prompt close across tabs via localstorage broadcast and prevent further
                const onClose = () =>
                {
                    if (closeable)
                    {
                        store.dispatch('setLayoutData', {employee_up_to_date: Object.assign({}, store.state.layoutData.employee_up_to_date, {prompt: false})})
                        .then(() => LocalStorage.set('employeeUpdateData', deepCopyObject(store.state.layoutData.employee_up_to_date)));
                    }
                };

                let popup = new Popup({
                    title: 'Persönliche Daten aktualisieren',
                    titleIcon: 'update',
                    size: 'medium',
                    component: Instance.default,
                    componentProps: {closeable},
                    closeable,
                    persistent: true,
                    onClose,
                });

                popup.open();
            });
        }

        if (store.state.personalDashboard?.needs)
        {
            const popup = await store.dispatch('createPopup', {
                size: 'long',
                title: 'Ihr Dashboard',
                titleIcon: 'dashboard',
                fullWidthOnMobile: true,
                id: 'personalDashboardPopup',
                component: markRaw(PersonalDashboardPopup),
                componentProps: {
                    afterLogin: true,
                }
            });

            popup.open();
        }
    }

    next();
});

export function initializeBuildUpdate()
{
    // dont refresh page in development mode
    if (process.env.NODE_ENV === 'development')
    {
        return;
    }

    // check every 5 minutes if build update released
    XHRManager.add(5, 'lastBuildUpdate', (err, data) =>
    {
        if (err)
        {
            return;
        }

        redirectNextRoute = data !== false && data !== window.__LAST_BUILD_UPDATE;
    });
}

setRouter(router);

export {
    hash,
};

export default router;